import { useState } from 'react'
import { FieldStatus } from '@ant-design/pro-components'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, App, Button, Descriptions, Drawer, Popconfirm, Space, Table, Tag } from 'antd'
import { formatShanghaiDateTime } from '@/const/date.ts'
import { DRAWER_SIZE } from '@/const/drawer.ts'
import { Authorized } from '@/features/authorization/authorized.tsx'
import { AttachmentUpload } from '@/features/files/index.ts'
import { BusinessTag } from '@/features/master-data/index.ts'
import { useAccess } from '@/hooks/use-access.ts'
import { useTRPC } from '@/trpc/react.ts'
import { formatAmount } from '@/utils/money.ts'
import { BillAdjustment, BillRefund } from './bill-adjustment.tsx'
import { BillEditor } from './bill-editor.tsx'
import { BillMoneyForm } from './bill-money-form.tsx'
import { billDate } from './bill-search.ts'
import {
  BILL_COLLECTION_STATUS,
  BILL_KIND_COLORS,
  BILL_KINDS,
  BILL_STATUS,
  BILL_STATUS_COLORS,
  MONEY_KIND_COLORS,
  MONEY_KIND_LABELS,
  MONEY_STATUS_COLORS,
  MONEY_STATUS_LABELS
} from './const/bill-search.ts'
import { toCents } from './utils/money.ts'
import { refreshMoneyQueries } from './utils/refresh-money-queries.ts'

function billSourceLabel(bill: {
  sourceOrderNo?: string | null
  rentalOrderId?: number | null
  serviceOrderId?: number | null
}) {
  if (bill.sourceOrderNo) {
    return bill.sourceOrderNo
  }
  if (bill.rentalOrderId) {
    return `租赁订单 #${bill.rentalOrderId}`
  }
  if (bill.serviceOrderId) {
    return `服务订单 #${bill.serviceOrderId}`
  }
  return '主体或场地费用'
}

function billMeteringDescription(bill: {
  feeType: string
  quantity?: string | null
  unitPrice?: string | null
  originalAmount?: string | null
  meteredUnitPrice?: string | null
}) {
  const unit = bill.feeType === 'water' ? '吨' : '度'
  if (bill.quantity != null && bill.unitPrice != null) {
    const amount = bill.originalAmount === null ? '待确认' : `¥${formatAmount(bill.originalAmount)}`
    return `${bill.quantity} ${unit} × ${formatAmount(bill.unitPrice)} 元/${unit} = ${amount}`
  }
  if (bill.originalAmount != null) {
    return '手工核定金额'
  }
  if (bill.meteredUnitPrice != null) {
    return `约定单价 ${formatAmount(bill.meteredUnitPrice)} 元/${unit}，待填写用量`
  }
  return '待核定金额'
}

function billEditorSource(rentalOrderId: number | null, serviceOrderId: number | null) {
  if (rentalOrderId) {
    return { type: 'rental' as const, orderId: rentalOrderId }
  }
  if (serviceOrderId) {
    return { type: 'service' as const, orderId: serviceOrderId }
  }
  return null
}

export function BillDetail({
  billId,
  canManage,
  onClose,
  initialAction,
  onDeleted
}: {
  onDeleted?: () => Promise<unknown>
  initialAction?: 'refund'
  billId: number
  canManage: boolean
  onClose: () => void
}) {
  const access = useAccess()
  const trpc = useTRPC()
  const [open, setOpen] = useState(true)
  const cache = useQueryClient()
  const { message } = App.useApp()
  const query = useQuery(trpc.bill.detail.queryOptions({ billId }))
  const [adjusting, setAdjusting] = useState(false)
  const [refunding, setRefunding] = useState(initialAction === 'refund')
  const [receipting, setReceipting] = useState(false)
  const issue = useMutation(trpc.bill.issue.mutationOptions())
  const remove = useMutation(trpc.bill.deleteDraft.mutationOptions())
  const [editing, setEditing] = useState(false)
  const bill = query.data
  const businessName = bill?.direction === 'payable' ? bill.expenseBusinessName : bill?.businessName
  const businessColor = bill?.direction === 'payable' ? bill.expenseBusinessColor : bill?.businessColor
  const CollectionStatus = FieldStatus[BILL_COLLECTION_STATUS[bill?.collectionStatus ?? 'notIssued'].status]
  const refresh = async () => refreshMoneyQueries(cache, trpc)
  const confirm = async () => {
    if (!bill) {
      return
    }
    try {
      const input = { billId, expectedUpdatedAt: bill.updatedAt }
      await issue.mutateAsync(input)
      await refresh()
      void message.success('账单已出账')
    } catch (error) {
      void message.error(error instanceof Error ? error.message : '操作失败')
    }
  }
  return (
    <>
      <Drawer
        open={open}
        title={'账单详情 ·  ' + (bill?.billNo ?? '')}
        size={DRAWER_SIZE}
        closable={{ placement: 'end' }}
        onClose={() => setOpen(false)}
        afterOpenChange={(visible) => {
          if (!visible) {
            onClose()
          }
        }}
        footer={
          bill && (
            <div className="flex min-h-10 flex-wrap items-center justify-end gap-3">
              {bill.status === 'issued' && (access.can('bill.adjust') || access.can('bill.refund.record')) && (
                <Space wrap className="justify-end">
                  <Authorized permissions={['bill.adjust']}>
                    <Button size="medium" onClick={() => setAdjusting(true)}>
                      调整金额
                    </Button>
                  </Authorized>
                  <Authorized permissions={['bill.refund.record']}>
                    <Button
                      size="medium"
                      disabled={toCents(bill.receivedAmount) <= toCents(bill.refundedAmount)}
                      onClick={() => setRefunding(true)}
                    >
                      {bill.direction === 'receivable' ? '登记退款' : '登记付款退回'}
                    </Button>
                  </Authorized>
                </Space>
              )}
              {bill.status === 'issued' &&
                Number(bill.outstandingAmount) > 0 &&
                access.can(bill.direction === 'receivable' ? 'bill.receipt.record' : 'bill.payment.record') && (
                  <Button size="medium" type="primary" onClick={() => setReceipting(true)}>
                    登记{bill.direction === 'receivable' ? '收款' : '付款'}
                  </Button>
                )}
              {bill.status === 'draft' && canManage && (
                <Space wrap className="justify-end">
                  <Authorized permissions={['bill.update']}>
                    <Button size="medium" onClick={() => setEditing(true)}>
                      编辑草稿
                    </Button>
                  </Authorized>
                  <Popconfirm title="确认出账？" description="出账后费用明细不可直接修改。" onConfirm={() => confirm()}>
                    <Authorized permissions={['bill.issue']}>
                      <Button size="medium" type="primary" loading={issue.isPending}>
                        确认出账
                      </Button>
                    </Authorized>
                  </Popconfirm>
                  <Popconfirm
                    title="删除草稿账单？"
                    description="仅删除未出账且无关联记录的草稿。规则账单可重新生成。"
                    okButtonProps={{ danger: true, loading: remove.isPending }}
                    onConfirm={async () => {
                      try {
                        await remove.mutateAsync({ billId, expectedUpdatedAt: bill.updatedAt })
                        setOpen(false)
                        await refreshMoneyQueries(cache, trpc)
                        await onDeleted?.()
                        void message.success('草稿账单已删除')
                      } catch (error) {
                        void message.error(error instanceof Error ? error.message : '删除失败')
                      }
                    }}
                  >
                    <Authorized permissions={['bill.delete']}>
                      <Button size="medium" danger loading={remove.isPending}>
                        删除草稿
                      </Button>
                    </Authorized>
                  </Popconfirm>
                </Space>
              )}
            </div>
          )
        }
        loading={query.isPending}
      >
        {query.isError && (
          <Alert
            type="error"
            showIcon
            title={query.error.message}
            action={<Button onClick={() => void query.refetch()}>重试</Button>}
          />
        )}
        {bill && (
          <>
            <Descriptions
              column={{ xs: 1, sm: 2 }}
              items={[
                {
                  key: 'status',
                  label: '账单状态',
                  children: <Tag color={BILL_STATUS_COLORS[bill.status]}>{BILL_STATUS[bill.status].text}</Tag>
                },
                {
                  key: 'collectionStatus',
                  label: '结算状态',
                  children: <CollectionStatus>{BILL_COLLECTION_STATUS[bill.collectionStatus].text}</CollectionStatus>
                },
                {
                  key: 'customer',
                  label: bill.direction === 'receivable' ? '客户' : '付款对象',
                  children: bill.customerName
                },
                { key: 'org', label: '经营主体', children: bill.organizationName },
                {
                  key: 'business',
                  label: bill.direction === 'payable' ? '支出业务' : '业务类型',
                  children: businessName ? (
                    <BusinessTag businessName={businessName} businessColor={businessColor} />
                  ) : (
                    '—'
                  )
                },
                ...(bill.direction === 'payable'
                  ? [
                      { key: 'paymentDetails', label: '银行转款信息', children: bill.paymentDetails || '—' },
                      { key: 'urgent', label: '付款优先级', children: bill.expenseUrgent ? '加急' : '普通' }
                    ]
                  : []),
                { key: 'salesperson', label: '业务员', children: bill.salespersonName || '—' },
                {
                  key: 'order',
                  label: '来源订单',
                  children: billSourceLabel(bill)
                },
                {
                  key: 'period',
                  label: '费用期间',
                  children: `${billDate(bill.periodStart)} 至 ${billDate(bill.periodEnd)}`
                },
                { key: 'due', label: '到期日期', children: billDate(bill.dueAt) },
                ...(['water', 'electricity'].includes(bill.feeType)
                  ? [
                      {
                        key: 'metering',
                        label: '水电计费依据',
                        children: billMeteringDescription(bill)
                      }
                    ]
                  : []),
                {
                  key: 'original',
                  label: '原始金额',
                  children: bill.originalAmount === null ? '待确认' : `¥${formatAmount(bill.originalAmount)}`
                },
                {
                  key: 'total',
                  label: '账单金额',
                  children: bill.totalAmount === null ? '待确认' : `¥${formatAmount(bill.totalAmount)}`
                },
                {
                  key: 'received',
                  label: bill.direction === 'receivable' ? '已收' : '已付',
                  children: `¥${formatAmount(bill.receivedAmount)}`
                },
                {
                  key: 'latestSettlementAt',
                  label: '最近收付日期',
                  children: bill.latestSettlementAt ? formatShanghaiDateTime(bill.latestSettlementAt) : '—'
                },
                {
                  key: 'outstanding',
                  label: bill.direction === 'receivable' ? '待收' : '待付',
                  children: bill.totalAmount === null ? '待确认' : `¥${formatAmount(bill.outstandingAmount)}`
                },
                {
                  key: 'refunded',
                  label: bill.direction === 'receivable' ? '已退款' : '已收回',
                  children: `¥${formatAmount(bill.refundedAmount)}`
                },
                {
                  key: 'refund',
                  label: bill.direction === 'receivable' ? '待退款' : '待收回',
                  children: `¥${formatAmount(bill.refundableAmount)}`
                },
                { key: 'note', label: '备注', children: bill.note || '—' }
              ]}
            />
            <p className="my-4 flex flex-wrap items-center gap-2">
              <Tag className="m-0!" color={BILL_KIND_COLORS[bill.feeType]}>
                {BILL_KINDS[bill.feeType]}
              </Tag>
              <span>{bill.description}</span>
            </p>
            <Table
              className="my-5"
              scroll={{ x: 'max-content' }}
              rowKey="id"
              pagination={false}
              dataSource={bill.allocations}
              size="small"
              columns={[
                {
                  title: '流水 ID',
                  render: (_, row) => (
                    <Button type="link" href={`/finance/money?id=${row.transactionId}`}>
                      #{row.transactionId}
                    </Button>
                  )
                },
                {
                  title: '类型',
                  render: (_, row) => <Tag color={MONEY_KIND_COLORS[row.kind]}>{MONEY_KIND_LABELS[row.kind]}</Tag>
                },
                {
                  title: '状态',
                  render: (_, row) => (
                    <Tag color={MONEY_STATUS_COLORS[row.status]}>{MONEY_STATUS_LABELS[row.status]}</Tag>
                  )
                },
                { title: '分配金额', dataIndex: 'amount', render: formatAmount },
                {
                  title: '实际时间',
                  render: (_, row) => formatShanghaiDateTime(row.occurredAt)
                },
                { title: '共同备注', dataIndex: 'transactionNote' },
                { title: '账单备注', dataIndex: 'note' },
                {
                  title: '回单',
                  render: (_, row) => {
                    if (row.attachmentIds.length === 0) return '—'
                    return (
                      <AttachmentUpload purpose="moneyAttachment" value={row.attachmentIds} listType="text" readOnly />
                    )
                  }
                }
              ]}
            />
            <Table
              className="my-5"
              scroll={{ x: 'max-content' }}
              rowKey="id"
              pagination={false}
              dataSource={bill.adjustments}
              size="small"
              columns={[
                { title: '金额调整', dataIndex: 'amount', render: formatAmount },
                { title: '原因', dataIndex: 'reason' },
                {
                  title: '生效时间',
                  render: (_, row) => formatShanghaiDateTime(row.effectiveAt)
                }
              ]}
            />
          </>
        )}
        {refunding && bill?.status === 'issued' && access.can('bill.refund.record') && (
          <BillRefund
            bill={bill}
            onClose={() => setRefunding(false)}
            onSaved={async () => {
              await refresh()
              setRefunding(false)
            }}
          />
        )}
      </Drawer>
      {adjusting && bill && (
        <BillAdjustment
          bill={bill}
          onClose={() => setAdjusting(false)}
          onSaved={async () => {
            await refresh()
            setAdjusting(false)
          }}
        />
      )}
      {receipting && bill && (
        <BillMoneyForm
          bill={bill}
          onClose={() => setReceipting(false)}
          onSaved={async () => {
            await refresh()
            setReceipting(false)
          }}
        />
      )}
      {editing && bill && (
        <BillEditor
          bill={bill}
          source={billEditorSource(bill.rentalOrderId, bill.serviceOrderId)}
          onClose={() => setEditing(false)}
          onSaved={() => setEditing(false)}
        />
      )}
    </>
  )
}
